import { useNavigate } from "react-router-dom";
import { Card, Badge } from "@vlab-corporation/design-system";
import { detectClassType, detectIsOt } from "@vlab-corporation/vlab-pilates-default-service/class";
import type { ClassWithCompletion } from "@/hooks/useClasses";
import { trackEvent, EVENT_NAMES } from "@/utils/analytics-tracking";

const STATUS_BADGE_VARIANT: Record<string, "info" | "warning" | "success" | "error"> = {
  scheduled: "info",
  reserved: "info",
  inProgress: "warning",
  completed: "success",
  incomplete: "error",
  cancelled: "error",
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: "예정",
  reserved: "예약",
  inProgress: "진행 중",
  completed: "완료",
  incomplete: "미완료",
  cancelled: "취소",
};

function getClassTypeLabel(maxTrainees?: number, title?: string): string {
  const ct = maxTrainees && maxTrainees > 0 ? detectClassType(maxTrainees) : "PRIVATE_REGULAR";
  const ot = title ? detectIsOt(title) : false;
  if (ct === "GROUP_REGULAR") return ot ? "그룹 OT" : "그룹 정규";
  return ot ? "1:1 OT" : "1:1 정규";
}

interface ClassCardProps {
  classItem: ClassWithCompletion;
  subdomain?: string;
  /** studiomate 수업의 "수업 관리"(상세/변경/예약취소/삭제) 진입. 미전달 시 버튼 숨김. */
  onManage?: () => void;
}

export function ClassCard({ classItem, subdomain, onManage }: ClassCardProps) {
  const navigate = useNavigate();

  // 체험단 카드: 일지 작성 불가, 정보 표시만
  if (classItem.type === "counsel") {
    return (
      <Card className="opacity-80">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge variant="warning">체험단</Badge>
              <span className="font-medium text-sm">{classItem.title}</span>
            </div>
            <span className="text-sm text-gray-500">
              {classItem.startTime} - {classItem.endTime}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>자동 정산 대상</span>
            <Badge variant="success">정산 포함</Badge>
          </div>
        </div>
      </Card>
    );
  }

  if (classItem.type === "studiomate") {
    return (
      <Card
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => {
          trackEvent(EVENT_NAMES.CLASS_SELECTED, { class_id: classItem.id });
          navigate(
            `/classes/${classItem.id}/journal${classItem.date ? `?date=${classItem.date}&from=/` : "?from=/"}`,
          );
        }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge variant={classItem.maxTrainees === 1 ? "info" : "warning"}>
                {getClassTypeLabel(classItem.maxTrainees, classItem.title)}
              </Badge>
              <span className="font-medium text-sm">{classItem.title}</span>
            </div>
            <span className="text-sm text-gray-500">
              {classItem.startTime} - {classItem.endTime}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              수강생 {classItem.currentTrainees}/{classItem.maxTrainees}명
            </span>
            {classItem.journalWritten ? (
              <Badge variant="success">일지 작성됨</Badge>
            ) : (
              <Badge variant="error" dot pulse>
                일지 미작성
              </Badge>
            )}
          </div>
          {classItem.bookings && classItem.bookings.length > 0 && (
            <div className="mt-2 text-xs text-gray-400">
              {classItem.bookings.map((b, i) => (
                <span key={i}>
                  {b.memberName}
                  {i < classItem.bookings!.length - 1 ? ", " : ""}
                </span>
              ))}
            </div>
          )}
          {onManage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onManage();
              }}
              className="mt-3 block w-full text-center text-xs text-gray-600 border border-gray-300 rounded-md py-1.5 hover:bg-gray-50 transition-colors"
            >
              수업 관리
            </button>
          )}
          {subdomain && (
            <a
              href={`https://${subdomain}.studiomate.kr/lectures`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-3 block text-center text-xs text-primary-600 border border-primary-300 rounded-md py-1.5 hover:bg-primary-50 transition-colors"
            >
              스튜디오메이트에서 확인
            </a>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => {
        trackEvent(EVENT_NAMES.CLASS_SELECTED, { class_id: classItem.id });
        navigate(`/classes/${classItem.id}/journal?from=/`);
      }}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={classItem.type === "regular" ? "info" : "warning"}>
              {classItem.type === "regular" ? "정규" : "OT"}
            </Badge>
            <Badge variant={STATUS_BADGE_VARIANT[classItem.status] ?? "info"}>
              {STATUS_LABEL[classItem.status] ?? classItem.status}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 truncate">
            {classItem.type === "regular"
              ? `회원: ${classItem.memberId}`
              : `리드: ${classItem.leadId}`}
          </p>
        </div>
        <div className="ml-3 flex-shrink-0">
          {classItem.journalWritten ? (
            <Badge variant="success">일지 작성됨</Badge>
          ) : (
            <Badge variant="error" dot pulse>
              일지 미작성
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}
